import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';

export type SecuritySummary = {
    eventCount: number;
    affectedUserCount: number;
    affectedClientCount: number;
    reviewRequiredUserCount: number;
    developerToolsDetectedCount: number;
    shortcutAttemptCount: number;
    anonymousEventCount: number;
};

export type SecurityDaily = { day: string; events: number; users: number; clients: number };
export type SecurityBreakdown = { name: string; events: number; users: number; eventRate: number };
export type SecurityAffectedUser = {
    userId: string; name: string; email: string; eventCount: number; clientCount: number;
    firstEventAtUtc: string; lastEventAtUtc: string; lastEventType: string; lastDetectionMethod: string;
    reviewStatus: string; recentEventCount: number; deviceClass: string; operatingSystem: string;
    browserName: string; countryCode: string;
};
export type SecurityRecentEvent = {
    securityEventId: string; userId: string; name: string; email: string; eventType: string;
    detectionMethod: string; route: string | null; deviceClass: string; clientKind: string;
    clientPlatform: string; operatingSystem: string; browserName: string; deviceModel: string | null;
    appVersion: string | null; countryCode: string; occurredAtUtc: string; receivedAtUtc: string;
};
export type ClientSecurityAnalyticsData = {
    generatedAtUtc: string; fromUtc: string; toUtc: string; dataQualityNote: string;
    anonymousTrackingStatus: string; summary: SecuritySummary; daily: SecurityDaily[];
    byEventType: SecurityBreakdown[]; byDetectionMethod: SecurityBreakdown[];
    byDeviceClass: SecurityBreakdown[]; byOperatingSystem: SecurityBreakdown[];
    byBrowser: SecurityBreakdown[]; byCountry: SecurityBreakdown[];
    affectedUsers: SecurityAffectedUser[]; affectedUserTotal: number; page: number; pageSize: number;
    recentEvents: SecurityRecentEvent[];
};

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const list = (value: unknown) => Array.isArray(value) ? value : [];
const text = (value: unknown) => typeof value === 'string' ? value : '';
const nullableText = (value: unknown) => typeof value === 'string' && value.trim() ? value : null;
const number = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0;

const breakdown = (value: unknown): SecurityBreakdown => {
    const row = record(value);
    return { name: text(row.name), events: number(row.events), users: number(row.users), eventRate: number(row.eventRate) };
};

function normalize(value: unknown): ClientSecurityAnalyticsData {
    const data = record(value);
    const summary = record(data.summary);
    return {
        generatedAtUtc: text(data.generatedAtUtc), fromUtc: text(data.fromUtc), toUtc: text(data.toUtc),
        dataQualityNote: text(data.dataQualityNote), anonymousTrackingStatus: text(data.anonymousTrackingStatus),
        summary: {
            eventCount: number(summary.eventCount), affectedUserCount: number(summary.affectedUserCount),
            affectedClientCount: number(summary.affectedClientCount), reviewRequiredUserCount: number(summary.reviewRequiredUserCount),
            developerToolsDetectedCount: number(summary.developerToolsDetectedCount), shortcutAttemptCount: number(summary.shortcutAttemptCount),
            anonymousEventCount: number(summary.anonymousEventCount),
        },
        daily: list(data.daily).map((value) => { const row = record(value); return { day: text(row.day), events: number(row.events), users: number(row.users), clients: number(row.clients) }; }),
        byEventType: list(data.byEventType).map(breakdown), byDetectionMethod: list(data.byDetectionMethod).map(breakdown),
        byDeviceClass: list(data.byDeviceClass).map(breakdown), byOperatingSystem: list(data.byOperatingSystem).map(breakdown),
        byBrowser: list(data.byBrowser).map(breakdown), byCountry: list(data.byCountry).map(breakdown),
        affectedUsers: list(data.affectedUsers).map((value) => { const row = record(value); return {
            userId: text(row.userId), name: text(row.name), email: text(row.email), eventCount: number(row.eventCount),
            clientCount: number(row.clientCount), firstEventAtUtc: text(row.firstEventAtUtc), lastEventAtUtc: text(row.lastEventAtUtc),
            lastEventType: text(row.lastEventType), lastDetectionMethod: text(row.lastDetectionMethod), reviewStatus: text(row.reviewStatus),
            recentEventCount: number(row.recentEventCount), deviceClass: text(row.deviceClass), operatingSystem: text(row.operatingSystem),
            browserName: text(row.browserName), countryCode: text(row.countryCode),
        }; }),
        affectedUserTotal: number(data.affectedUserTotal), page: number(data.page), pageSize: number(data.pageSize),
        recentEvents: list(data.recentEvents).map((value) => { const row = record(value); return {
            securityEventId: text(row.securityEventId), userId: text(row.userId), name: text(row.name), email: text(row.email),
            eventType: text(row.eventType), detectionMethod: text(row.detectionMethod), route: nullableText(row.route),
            deviceClass: text(row.deviceClass), clientKind: text(row.clientKind), clientPlatform: text(row.clientPlatform),
            operatingSystem: text(row.operatingSystem), browserName: text(row.browserName), deviceModel: nullableText(row.deviceModel),
            appVersion: nullableText(row.appVersion), countryCode: text(row.countryCode), occurredAtUtc: text(row.occurredAtUtc),
            receivedAtUtc: text(row.receivedAtUtc),
        }; }),
    };
}

export async function getClientSecurityAnalytics(query: {
    fromUtc: string; toUtc: string; eventType: string; search: string; page: number; pageSize: number;
}) {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const configuredBase = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const version = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const root = /\/api\/v[^/]+$/i.test(configuredBase) ? configuredBase : `${configuredBase}/api/v${version}`;
    const url = new URL(`${root}/AdminClientSecurityAnalytics`);
    Object.entries(query).forEach(([key, value]) => {
        if (String(value).trim()) url.searchParams.set(key, String(value));
    });
    const token = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(url, { headers });
    const payload: unknown = (response.headers.get('content-type') ?? '').includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(typeof payload === 'string' ? payload : `Security analytics request failed (${response.status})`);
    const envelope = record(payload);
    return normalize('data' in envelope ? envelope.data : payload);
}
